import React from 'react';

const APPLE_URL = 'https://apps.apple.com/us/app/whapaxx-gaming/id6514299783';
const GOOGLE_URL = 'https://play.google.com/store/apps/details?id=com.whapaxx.customer2';

const AppleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.18 23.48c-.36-.17-.59-.52-.59-.92V1.44c0-.4.23-.75.59-.92l11.68 11.48L3.18 23.48zm1.39-22.7L15.89 12 4.57.78zm13.85 12.61L16.6 12l1.82-1.39 3.23 1.84c.55.31.55.79 0 1.1l-3.23 1.84zM4.57 23.22L15.89 12l-1.82-1.39L4.57 23.22z"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export function getStoreUrl() {
  if (typeof navigator === 'undefined') return APPLE_URL;
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return GOOGLE_URL;
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return APPLE_URL;
  // Desktop: check platform for a best guess, default to Apple
  if (/Win|Linux/.test(navigator.platform)) return GOOGLE_URL;
  return APPLE_URL;
}

/**
 * Renders App Store + Google Play links using the site's cyberpunk neon style.
 * variant: "smart" (single device-aware button) | "buttons" (two neon filled) |
 *          "inline" (small text links) | "pill" (compact pills) | "icons" (icon-only)
 */
export default function StoreLinks({ variant = 'smart', label = 'Download App', style: wrapStyle = {} }) {
  if (variant === 'smart') {
    return (
      <div style={{ display: 'inline-flex', ...wrapStyle }}>
        <a
          href={APPLE_URL}
          onClick={(e) => { e.preventDefault(); window.open(getStoreUrl(), '_blank'); }}
          className="btn-neon btn-neon--filled"
          style={s.smartBtn}
        >
          <span style={s.btnInner}><DownloadIcon /> {label}</span>
        </a>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <span style={{ display: 'inline-flex', gap: '0.75rem', ...wrapStyle }}>
        <a href={APPLE_URL} target="_blank" rel="noopener noreferrer" style={s.inlineLink}>
          <AppleIcon /> App Store
        </a>
        <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" style={s.inlineLink}>
          <GoogleIcon /> Google Play
        </a>
      </span>
    );
  }

  if (variant === 'icons') {
    return (
      <div style={{ display: 'flex', gap: '6px', ...wrapStyle }}>
        <a href={APPLE_URL} target="_blank" rel="noopener noreferrer" className="btn-neon" style={s.iconBtn} title="Download on the App Store">
          <span><AppleIcon /></span>
        </a>
        <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" className="btn-neon" style={s.iconBtn} title="Get it on Google Play">
          <span><GoogleIcon /></span>
        </a>
      </div>
    );
  }

  if (variant === 'pill') {
    return (
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', ...wrapStyle }}>
        <a href={APPLE_URL} target="_blank" rel="noopener noreferrer" className="btn-neon" style={s.pill}>
          <span style={s.pillInner}><AppleIcon /> App Store</span>
        </a>
        <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" className="btn-neon" style={s.pill}>
          <span style={s.pillInner}><GoogleIcon /> Google Play</span>
        </a>
      </div>
    );
  }

  // Default: neon cyberpunk buttons
  return (
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', ...wrapStyle }}>
      <a href={APPLE_URL} target="_blank" rel="noopener noreferrer" className="btn-neon btn-neon--filled" style={s.btn}>
        <span style={s.btnInner}><AppleIcon /> App Store</span>
      </a>
      <a href={GOOGLE_URL} target="_blank" rel="noopener noreferrer" className="btn-neon btn-neon--filled" style={s.btn}>
        <span style={s.btnInner}><GoogleIcon /> Google Play</span>
      </a>
    </div>
  );
}

const s = {
  smartBtn: {
    textDecoration: 'none',
    padding: '12px 28px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  iconBtn: {
    textDecoration: 'none',
    padding: '6px 10px',
    fontSize: '0.85rem',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  btn: {
    textDecoration: 'none',
    padding: '12px 28px',
    fontSize: '0.85rem',
  },
  btnInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  pill: {
    textDecoration: 'none',
    padding: '6px 14px',
    fontSize: '0.75rem',
  },
  pillInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  inlineLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: '#12d0ff',
    fontSize: '0.85rem',
    fontWeight: 600,
    textDecoration: 'none',
    textShadow: '0 0 8px rgba(18,208,255,0.3)',
  },
};

export { APPLE_URL, GOOGLE_URL };
